import getWindow from 'Utils/get-window';

import { AdSize, SizeMappingEntry } from './gpt';
import { isFlintHeaderBiddingEnabled } from './ads-config';
import { FlintCachedAd, cacheFlintAd } from './flint-render';
import {
  GppData,
  getGppConsent,
  getTcfConsentString,
  getUspConsentString,
} from './consent';

/**
 * flint — Flipboard's in-house server-side header bidder (OpenRTB,
 * first-price) at ad.flipboard.com. This module mirrors what the mobile
 * clients do (iOS FLFlintAdRequest / Android AdHelper): before the GAM
 * request for a display slot, POST to flint's /query endpoint with the
 * slot's size (`ad_formats=display_s<w>x<h>`) and the user's consent
 * strings, and translate the winning bid in the response into the GAM
 * key-values that the FLCPM price-priority line items target:
 *
 *   FLCPM        — tiered winning price, integer CPM in CENTS (flint's
 *                  price-tier output; e.g. 125 = $1.25) — the bucket the
 *                  GAM line items key on
 *   flint_bidder — winning vendor (e.g. nimbus_omp, ttd_omp)
 *   flint_ad_id  — flint's ad id, for reporting
 *
 * Server support: flint has a `web.uid` AppInfo (deviceId 7, header bidding
 * enabled from version 1.0.0.0) and its FlintHandler already answers CORS
 * for https://flipboard.com (production) / *.flipboard.com (beta), so the
 * browser can call it directly — no proxy needed. The request is kept
 * "simple" (POST, params on the query string, no custom headers) so no
 * preflight is required.
 *
 * Everything fails open: on timeout (default 800ms), network error, adblock,
 * missing consent APIs, or a no-bid response the caller gets {} and the GPT
 * request proceeds without flint keys.
 */

export const FLINT_QUERY_URL = 'https://ad.flipboard.com/query';
export const FLINT_TIMEOUT_MS = 800;

// flint identifies clients by device name + app version; `web` is flint's
// device 7 and the web.uid AppInfo enables header bidding from 1.0.0.0.
const FLINT_DEVICE = 'web';
const FLINT_VERSION = '1.0.0.0';

export type FlintTargeting = Record<string, string>;

export type FlintQueryOptions = {
  // Base creative sizes for the slot (same values passed to defineSlot).
  sizes: AdSize[];
  // Optional responsive size mapping (same as defineSizeMapping); when
  // present the flint size is picked from the active viewport entry.
  sizeMapping?: SizeMappingEntry[];
  // GPT page/slot targeting context (page_type / topic_slug key-values).
  pageType?: string;
  topicSlug?: string;
  timeoutMs?: number;
  // Extended-ID (identity) provider — resolves the value for flint's `eids`
  // request param (`<source>,<id>` — the format the mobile clients send and
  // FullPageAndNativeAdQueryRequest parses into OpenRTB user.ext.eids), or
  // null when no identity is available. Supplied by the ads identity module
  // (./identity.ts, WEB_ADS_IDENTITY) and awaited inside flint's existing
  // time budget alongside the consent reads — never adds latency beyond it.
  eidsProvider?: () => Promise<string | null>;
};

type ConsentWindow = {
  innerWidth?: number;
  innerHeight?: number;
  fetch?: typeof fetch;
  location?: {
    origin?: string;
    pathname?: string;
  };
};

const getConsentWindow = (): ConsentWindow =>
  getWindow() as unknown as ConsentWindow;

/**
 * Pick the single creative size flint should bid on for this slot. flint's
 * ad_formats grammar (`display_s<w>x<h>`) carries exactly one size, so we
 * mirror GPT's size-mapping selection: the entry with the largest viewport
 * that fits the current one wins, and its primary (first) size is used.
 * Returns null when the active mapping entry has no sizes (slot suppressed
 * at this viewport).
 */
export const pickFlintSize = (
  sizes: AdSize[],
  sizeMapping?: SizeMappingEntry[],
  viewportWidth?: number,
): AdSize | null => {
  if (!sizeMapping || sizeMapping.length === 0) {
    return sizes[0] || null;
  }
  const width = viewportWidth ?? getConsentWindow().innerWidth ?? 0;
  const active = [...sizeMapping]
    .sort((a, b) => b.viewport[0] - a.viewport[0])
    .find((entry) => entry.viewport[0] <= width);
  if (!active) {
    return sizes[0] || null;
  }
  return active.sizes[0] || null;
};

/**
 * Build the flint /query URL for a slot. Parameter names follow the mobile
 * clients (FLFlintAdRequest / QueryRequestParameters): device + ver identify
 * the app, ad_formats narrows the auction to this slot's display size, and
 * gdpr_strings / ccpa_strings / gpp_strings carry the IAB consent strings
 * (a single un-keyed value applies to all vendors, matching mobile). `eids`
 * carries extended IDs as `<source>,<id>` pairs (semicolon-separated) — the
 * exact format Android sends ("liveramp.com,<envelope>", FlintClient.kt) and
 * iOS sends (kFLLiveRampIDParameterName = "eids", FLFlintAdRequest.m) —
 * which flint parses into OpenRTB user.ext.eids for bidders
 * (FullPageAndNativeAdQueryRequest.java).
 */
export const buildFlintQueryUrl = (
  size: AdSize,
  options: FlintQueryOptions,
  consent: {
    tcString?: string | null;
    uspString?: string | null;
    gpp?: GppData | null;
    eids?: string | null;
  },
): string => {
  const params = new URLSearchParams();
  params.set('device', FLINT_DEVICE);
  params.set('ver', FLINT_VERSION);
  params.set('ad_formats', `display_s${size[0]}x${size[1]}`);
  // flint's context vocabulary is section/article; web item pages map to
  // article, every other ad page (topic/magazine/profile grids) to section.
  params.set('context', options.pageType === 'item' ? 'article' : 'section');
  if (options.topicSlug) {
    // Topic feed id convention used across clients (the %2F is part of the
    // section id itself, not URL encoding).
    params.set('feed_id', `flipboard/topic%2F${options.topicSlug}`);
  }
  // The URL of the page hosting the slot — flint forwards it to bidders as
  // OpenRTB site.page (ThirdPartyAdSource.composeSiteObject reads the
  // request's page_url), and bidders bid materially better with a real page
  // URL. Origin + path only: the query string / hash can carry volatile or
  // sensitive params (?ff= flag overrides etc.) that must not leave the
  // page. Contextual (describes the page, not the person) → consent-neutral.
  const location = getConsentWindow().location;
  if (location?.origin && location.pathname) {
    params.set('page_url', `${location.origin}${location.pathname}`);
  }
  if (consent.tcString) {
    params.set('gdpr_strings', consent.tcString);
  }
  if (consent.uspString) {
    params.set('ccpa_strings', consent.uspString);
  }
  if (consent.gpp?.gppString) {
    params.set('gpp_strings', consent.gpp.gppString);
    if (consent.gpp.applicableSections?.length) {
      params.set('gpp_sids', consent.gpp.applicableSections.join(','));
    }
  }
  if (consent.eids) {
    params.set('eids', consent.eids);
  }
  return `${FLINT_QUERY_URL}?${params.toString()}`;
};

/**
 * Map a flint /query response body to GAM targeting key-values — exactly the
 * keys Android's AdHelper sets from a flint win. A response without a
 * header-bidding price (flcpm) contributes nothing: direct-sold flint
 * formats are not renderable on web, and the FLCPM line items are the only
 * GAM hook.
 */
export const flintTargetingFromResponse = (
  response: unknown,
): FlintTargeting => {
  const body = response as {
    ad?: Record<string, unknown>;
    ads?: Array<Record<string, unknown>>;
  } | null;
  const ad = body?.ad || (Array.isArray(body?.ads) ? body?.ads[0] : null);
  if (!ad) {
    return {};
  }
  const flcpm = ad.flcpm;
  let flcpmValue: string | null = null;
  if (typeof flcpm === 'number' && flcpm > 0) {
    flcpmValue = String(flcpm);
  } else if (typeof flcpm === 'string' && flcpm) {
    flcpmValue = flcpm;
  }
  if (!flcpmValue) {
    return {};
  }
  const targeting: FlintTargeting = { FLCPM: flcpmValue };
  if (typeof ad.flint_bidder === 'string' && ad.flint_bidder) {
    targeting.flint_bidder = ad.flint_bidder;
  }
  if (
    (typeof ad.flint_ad_id === 'string' && ad.flint_ad_id) ||
    typeof ad.flint_ad_id === 'number'
  ) {
    targeting.flint_ad_id = String(ad.flint_ad_id);
  }
  return targeting;
};

/**
 * Extract the winning bid's renderable creative from a flint /query
 * response, for the render-from-cache path (./flint-render.ts).
 *
 * Authoritative response shape (flint server): for rich_media_third_party
 * (banner) ads, StandardAdPackage sets item.type = "mraid" and serializes
 * the item onto the ad object; the HTML markup is item.script — the winning
 * OpenRTB bid's `adm` (ThirdPartyAdSource composes
 * {script: adm, altSize: {width, height}}) — and item.size carries the
 * creative dimensions. The mobile clients read the same field (iOS
 * kFLMRAIDItemHTMLKey = "script"). Dimensions fall back to the requested
 * slot size when absent. Returns null for anything that is not a renderable
 * mraid ad with a flint_ad_id.
 */
export const extractFlintAdMarkup = (
  response: unknown,
  requestedSize?: AdSize | null,
): ({ flintAdId: string } & FlintCachedAd) | null => {
  const body = response as {
    ad?: Record<string, unknown>;
    ads?: Array<Record<string, unknown>>;
  } | null;
  const ad = body?.ad || (Array.isArray(body?.ads) ? body?.ads[0] : null);
  if (!ad) {
    return null;
  }
  const hasAdId =
    (typeof ad.flint_ad_id === 'string' && !!ad.flint_ad_id) ||
    typeof ad.flint_ad_id === 'number';
  const flintAdId = hasAdId ? String(ad.flint_ad_id) : null;
  const item = ad.item as {
    type?: unknown;
    script?: unknown;
    size?: { width?: unknown; height?: unknown } | null;
  } | null;
  if (
    !flintAdId ||
    !item ||
    item.type !== 'mraid' ||
    typeof item.script !== 'string' ||
    !item.script
  ) {
    return null;
  }
  const width =
    (typeof item.size?.width === 'number' && item.size.width) ||
    requestedSize?.[0];
  const height =
    (typeof item.size?.height === 'number' && item.size.height) ||
    requestedSize?.[1];
  if (!width || !height) {
    return null;
  }
  return { flintAdId, markup: item.script, width, height };
};

const queryFlint = async (
  options: FlintQueryOptions,
  signal?: AbortSignal,
): Promise<FlintTargeting> => {
  const size = pickFlintSize(options.sizes, options.sizeMapping);
  if (!size) {
    return {};
  }
  const [tcString, uspString, gpp, eids] = await Promise.all([
    getTcfConsentString(),
    getUspConsentString(),
    getGppConsent(),
    // Identity (WEB_ADS_IDENTITY): the provider resolves the cached LiveRamp
    // envelope as `liveramp.com,<envelope>` or null (flag off, no consent,
    // no identity, or not resolved yet) — it caps its own wait so it never
    // outlasts the consent reads. Fail open: a provider error means no eids.
    options.eidsProvider ? options.eidsProvider().catch(() => null) : null,
  ]);
  const url = buildFlintQueryUrl(size, options, {
    tcString,
    uspString,
    gpp,
    eids,
  });
  // Simple cross-origin POST (no body / custom headers → no preflight);
  // flint's FlintHandler allows the flipboard.com origin. fetch is read off
  // the window so SSR (getWindow() → {}) is a no-op.
  const win = getConsentWindow();
  if (typeof win.fetch !== 'function') {
    return {};
  }
  const response = await win.fetch(url, {
    method: 'POST',
    credentials: 'omit',
    signal,
  });
  if (!response.ok) {
    return {};
  }
  const body: unknown = await response.json();
  const targeting = flintTargetingFromResponse(body);
  // Render-from-cache: keep the winning bid's markup client-side so the
  // universal creative on the FLCPM line items can render it if flint wins
  // the GAM auction (see ./flint-render.ts). Only header-bidding wins
  // (FLCPM present → targeting non-empty) are cacheable.
  if (targeting.flint_ad_id) {
    const cacheable = extractFlintAdMarkup(body, size);
    if (cacheable) {
      cacheFlintAd(cacheable.flintAdId, {
        markup: cacheable.markup,
        width: cacheable.width,
        height: cacheable.height,
      });
    }
  }
  return targeting;
};

/**
 * Fetch flint targeting for a slot with a hard time budget. Resolves with {}
 * (never rejects) on timeout or any failure, so callers can always proceed
 * with the GPT request.
 */
export const fetchFlintTargeting = async (
  options: FlintQueryOptions,
): Promise<FlintTargeting> => {
  const timeoutMs = options.timeoutMs ?? FLINT_TIMEOUT_MS;
  const controller =
    typeof AbortController === 'function' ? new AbortController() : null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race([
      queryFlint(options, controller?.signal),
      new Promise<FlintTargeting>((resolve) => {
        timer = setTimeout(() => {
          controller?.abort();
          resolve({});
        }, timeoutMs);
      }),
    ]);
  } catch (_) {
    // fail open: timeout/abort, network error, adblock, malformed JSON
    return {};
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
};

/**
 * Flag-gated entry point used by the ad slot: resolves immediately with {}
 * unless both WEB_ADS and WEB_ADS_FLINT are active.
 */
export const maybeFetchFlintTargeting = (
  featureFlags: Partial<Record<string, boolean>> | undefined,
  options: FlintQueryOptions,
): Promise<FlintTargeting> => {
  if (!isFlintHeaderBiddingEnabled(featureFlags)) {
    return Promise.resolve({});
  }
  return fetchFlintTargeting(options);
};
